import { api } from '../api/client';

export type RewardPerProgram = {
  programId: string;
  programName: string | null;
  total: number;
};

export type RewardSummary = {
  /** False when the account has rewards switched off. Nothing about them should be drawn. */
  enabled: boolean;
  name: string;
  icon: string;
  /** What the learner has now: everything collected, less anything spent. */
  total: number;
  /** Everything ever collected. Spending never reduces this. */
  earned: number;
  spent: number;
  /** Whether there is anything to spend them on. False leaves the total a plain figure. */
  spending: boolean;
  byProgram: RewardPerProgram[];
};

export type RewardItem = {
  id: string;
  name: string;
  cost: number;
};

export type Collected = {
  id: string;
  itemName: string;
  cost: number;
  balance: number;
};

export async function fetchRewardItems(): Promise<RewardItem[]> {
  return api<RewardItem[]>('/rewards/items', { token: true });
}

export async function addRewardItem(name: string, cost: number): Promise<RewardItem> {
  return api<RewardItem>('/rewards/items', {
    method: 'POST',
    token: true,
    body: { name, cost },
  });
}

export async function saveRewardItem(id: string, name: string, cost: number): Promise<RewardItem> {
  return api<RewardItem>(`/rewards/items/${encodeURIComponent(id)}`, {
    method: 'PUT',
    token: true,
    body: { name, cost },
  });
}

export async function removeRewardItem(id: string): Promise<void> {
  return api<void>(`/rewards/items/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    token: true,
  });
}

export async function redeemReward(itemId: string, learnerId: string | null): Promise<Collected> {
  return api<Collected>('/rewards/redeem', {
    method: 'POST',
    token: true,
    body: { itemId, learnerId },
  });
}

export type Redemption = {
  id: string;
  itemName: string;
  cost: number;
  collectedAt: string;
};

export async function fetchRedemptions(learnerId: string | null): Promise<Redemption[]> {
  return api<Redemption[]>(
    learnerId
      ? `/rewards/redemptions?learnerId=${encodeURIComponent(learnerId)}`
      : '/rewards/redemptions',
    { token: true },
  );
}

/**
 * What this learner has collected.
 *
 * Returns null rather than throwing: the markers are decoration on a page that has to work
 * without them, so a failure here should cost the rewards, not the programs beside them.
 */
export async function fetchRewardSummary(learnerId: string | null): Promise<RewardSummary | null> {
  try {
    return await api<RewardSummary>(
      learnerId ? `/rewards?learnerId=${encodeURIComponent(learnerId)}` : '/rewards',
      { token: true },
    );
  } catch {
    return null;
  }
}

export async function approveReward(programId: string): Promise<{ amount: number; total: number }> {
  return api<{ amount: number; total: number }>('/rewards/approve', {
    method: 'POST',
    token: true,
    body: { programId },
  });
}

/**
 * Tell the server a sitting has ended.
 *
 * Best effort by nature — a closed tab never sends it. Deliberately swallows failure: the
 * learner has already left, there is nothing on screen to show an error on, and the every-N
 * rule works out what it owes from the trials themselves regardless of this call.
 */
export async function endSession(
  programId: string,
  sessionId: string,
  answeredSomething: boolean,
): Promise<number> {
  try {
    const r = await api<{ rewarded: number }>('/games/session/end', {
      method: 'POST',
      token: true,
      body: { programId, sessionId, answeredSomething },
    });
    return r?.rewarded ?? 0;
  } catch {
    return 0;
  }
}
